import React, {useState, useEffect, useCallback, useRef} from "react";
import useCountriesData from "../../hooks/useContriesData";
import { MAX_BOUNDS, MIN_ZOOM, MAX_ZOOM, DEFAULT_MAP_STATE} from "../../config/config";
import { WebMercatorViewport } from "react-map-gl";
import useComponentSize from "@rehooks/component-size";
import DeckGL, { GeoJsonLayer } from "deck.gl";
import MapControls from "../common/map/MapControls";
import Accordion from "../common/accordion/Accordion";
import CountryDataDetail from "../common/popup/CountryDataDetail";
import MapToolTip from "../common/mapTooltip/MapToolTip";
import styles from "./country-data-map.module.scss";

import { data as privateWealthData } from "../data/data";


const CountryDataMap = () => {
  const [dataGroup, setDataGroup] = useState([]);
  const [hoveredCity, setHoveredCity] = useState(null);
  const [allCitiesData, setAllCitiesData] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [unselectedCountries, setUnselectedCountries] = useState([]);
  const [showId, setShowId] = useState();
  const [minZoom, setMinZoom] = useState(MIN_ZOOM);
  const [detailCard, setDetailCard] = useState('');
  const [showCard, setShowCard] = useState(false);
  const [viewport, setViewport] = useState(DEFAULT_MAP_STATE);
  let mapRef = useRef(null);
  let { width, height } = useComponentSize(mapRef);
  const popUpRef = useRef(null)
  const countriesData = useCountriesData();


  useEffect(() => {
    if (width && height) {
      const newViewport = new WebMercatorViewport({
        width: width,
        height: height,
        maxZoom: MAX_ZOOM,
        minZoom: MIN_ZOOM,
        zoom: 0,
        longitude: 0,
        latitude: 0,
      }).fitBounds(MAX_BOUNDS);

      setMinZoom(newViewport.zoom);
      setViewport(newViewport);
    }
  }, [width, height]);

  const handleViewStateChange = useCallback((newState) => {
    if (newState.oldViewState !== null) {
      // clamp the zoom
      newState.viewState.zoom =
        newState.viewState.zoom > MAX_ZOOM ? MAX_ZOOM : newState.viewState.zoom;
      newState.viewState.zoom =
        newState.viewState.zoom < MIN_ZOOM ? MIN_ZOOM : newState.viewState.zoom;
      setViewport((v) =>
        v
          ? new WebMercatorViewport({
              ...v,
              ...newState.viewState,
            })
          : null
      );
    }
  }, []);

  const resetZoom = useCallback(() => {
    const newViewport = new WebMercatorViewport({
      width: width,
      height: height,
      maxZoom: MAX_ZOOM,
      minZoom: MIN_ZOOM,
      zoom: 0,
      longitude: 0,
      latitude: 0,
    }).fitBounds(MAX_BOUNDS);

    setViewport(newViewport);
  }, [width, height]);

  const zoomIncrement = useCallback(
    (amt) => {
      const newZoom = Math.max(
        Math.min(Math.max(viewport.zoom + amt, 0.001), MAX_ZOOM),
        minZoom
      );
      handleViewStateChange({
        viewState: {
          zoom: newZoom,
          transitionDuration: 5000,
        },
      });
    },
    [viewport, handleViewStateChange, minZoom]
  );


  useEffect(() => {

    const showVisibleCountries = (includeCountries, features) =>{
      const filteredFeatures = features.filter((feature) =>
        includeCountries.includes(feature.properties.ISO3)
      );
      const visibleCountries = {
        type: "FeatureCollection",
        features: filteredFeatures,
      };
      setSelectedCountries(visibleCountries);
    }

    const hideNotVisibleCountries = (includeCountries, features) =>{
        const unfilteredFeatures = features.filter(
          (feature) => !includeCountries.includes(feature.properties.ISO3)
        );
        const notVisibleCountries = {
          type: "FeatureCollection",
          features: unfilteredFeatures,
        };
        setUnselectedCountries(notVisibleCountries);
    }
    
    if(privateWealthData && countriesData){
      let features = countriesData.features;

      let featuresMap = [];
      for(let i = 0; i <= features.length; i++){

        if(features[i] && features[i].properties.ISO3){
          let name = features[i].properties.ISO3;
          let longitude = features[i].properties.LON;
          let latitude = features[i].properties.LAT;

          featuresMap[name] = {
            longitude: longitude,
            latitude: latitude
          }
        }else{
          //console.log(features[i] + 'is undefined');
        }
      }

      const data = privateWealthData.filter(row => row.region!== null).map((item) => {
          let countryCode = item.ISO3;
          let lon = '';
          let lat = '';

          lon = featuresMap[countryCode].longitude;
          lat = featuresMap[countryCode].latitude
         
          return {
            ...item,
            longitude: lon,
            latitude: lat,
          };
      });

      const groupBy = prop => data => {
        return data.reduce((dict, item) => {
          const { [prop]: _, ...rest } = item;
          dict[item[prop]] = [...(dict[item[prop]] || []), rest];
          return dict;
        }, {});
      };
      
      const dataByRegion = Object.entries(groupBy('region')(data))
        .map(([key, value]) => ({ name: key, countries: value }));

      const topTenObj = {
        name: 'W10',
        countries: data.filter(item => item.W10 !== false).sort((a, b) => a.rank - b.rank)
      }

      topTenObj.countries.forEach(object => {
        delete object['region'];
      });

     const dataByGroup = [topTenObj].concat(dataByRegion);

     const includeCountries = data.map((d) => {return d.ISO3;});
 
      setAllCitiesData(data);
      setDataGroup(dataByGroup);
      showVisibleCountries(includeCountries, features);
      hideNotVisibleCountries(includeCountries, features);

    }
  }, [countriesData]);

  const getDataViaMap = (mapData, sourceData) =>{
    const targetData = sourceData.filter(item => item.ISO3 === mapData.object.properties.ISO3);
    return targetData[0];
  }
  
  const handleHover = (data, fromMap = false) => {
 
    setHoveredCity(data !== null ? data.country : null);
   
  };

  const handleMapHover = (data) => {
    // console.log(data);
    // console.log(getDataViaMap(data, allCitiesData).country);
    setHoveredCity(data.object ? getDataViaMap(data, allCitiesData).country : null);
  };

  const getProjectionCoordinates = (coords) => {
    if (viewport) {
      return viewport.project([coords[0], coords[1]]);
    }
    return [];
  };

  const handleShow = (id) => {
    setShowId(showId === id ? -1 : id);
  }

  const handleClick = (data, fromMap=false) =>{
    setDetailCard(fromMap? getDataViaMap(data, allCitiesData) : data);
    setShowCard(true);
    popUpRef.current.scrollIntoView({ behavior: "smooth" });
  }

  const cardClose = () =>{
    setShowCard(false);
  }



  return (
    <div className="appContainer">
      <div className="container-fluid px-0">
        <div className="row flex-md-row-reverse">
         
          <div className="col-md-8 col-lg-9">
            <p>The population and wealth data for the W10 — the world’s 10 wealthiest countries at year-end 2022 by HNWI population — and other key countries provides an overview of the global distribution of private wealth. Five of the W10 countries host <a href="https://www.henleyglobal.com/countries" target="_blank" rel="noreferrer" ><u>investment migration</u></a> programs, encouraging foreign direct investment in return for <a href="https://www.henleyglobal.com/residence-investment" target="_blank" rel="noreferrer"><u>residence</u></a> rights.</p>
          </div>
          <div className="col-md-4 col-lg-3">
            <h3>Click on the map or expand the lists below to see detailed country wealth information</h3>
          </div>
        </div>
      
        <div className="row flex-md-row-reverse">
          
          <div className="col-md-8 col-lg-9">
            <div className={styles.mapColumn}>
              <div className={styles.mapWrapper} ref={mapRef}>
                <DeckGL
                  viewState={viewport}
                  controller={{scrollZoom:false, doubleClickZoom: false}}
                  onViewStateChange={handleViewStateChange}
                >
                  <GeoJsonLayer
                    id="map-unselected-countries"
                    data={unselectedCountries ? unselectedCountries : null}
                    getLineColor={[255, 255, 255]}
                    getFillColor={[233, 233, 233]}
                    lineWidthUnits={"pixels"}
                    stroked={true}
                  />
                  <GeoJsonLayer
                    id="map-selected-countries"
                    data={selectedCountries ? selectedCountries : null}
                    getLineColor={[255, 255, 255]}
                    getFillColor={[64, 83, 99]}
                    lineWidthUnits={"pixels"}
                    stroked={true}
                    pickable={true}
                    onClick={(info) => handleClick(info, true)}
                    onHover={(info) => handleMapHover(info)}
                  />
            
                </DeckGL>
            
                {allCitiesData &&
                  allCitiesData.map((d) => {
                    return (
                      <React.Fragment key={d.country + "-tooltip"}>
                        {hoveredCity === d.country && (
                          <MapToolTip 
                            name={d.country} 
                            top={getProjectionCoordinates([d.longitude,d.latitude,])[1] - 10}
                            left={getProjectionCoordinates([d.longitude,d.latitude,])[0] - 10}
                          />
                      
                        )}
                      </React.Fragment>
                    );
                  })}
                  <MapControls
                    zoomIncrement={() => zoomIncrement(0.5)}
                    zoomDecrement={() => zoomIncrement(-0.5)}
                    resetZoom={() => resetZoom()}
                  /> 
              </div>
              <div ref={popUpRef} className={styles.positionToScroll}></div>
              {showCard ? <CountryDataDetail item={detailCard} onClose={cardClose} /> : null}
            </div>
           
          </div>
          <div className="col-md-4 col-lg-3 mt-4 mt-md-0">
            {dataGroup &&
                  dataGroup.map((d,index) => {
                  const listOfCountires = d.countries;
            return(
              <Accordion id={index} show={showId === index} onShow={()=>handleShow(index)} key={d.name}  title={d.name}>
                
                  {listOfCountires.map((item)=>{
                    return(
                      <div
                        key={item.country}
                        style={{
                          cursor:'pointer',
                          backgroundColor:
                            hoveredCity === item.country ? "#f7f7f7" : null,
                        }}
                        onMouseOver={() => {
                          handleHover(item);
                        }}
                        onMouseOut={() => {
                          handleHover(null);
                        }}
                        onClick={()=>handleClick(item)}
                      >
                        {item.country}
                      </div>
                    )
                  })}
              
              </Accordion>

            );
          })}
          </div>
        </div>

        <div className={`${styles.footerNote} mt-5`}>
          <ul>
            <li>HNWI figures rounded to nearest 100.</li>
            <li>‘Millionaires’ or ‘high-net-worth individuals’ or ‘HNWIs’ refer to individuals with investable wealth of USD 1 million or more.</li>
          </ul>
          <p>Sources: Population data: UNFPA <a href="https://www.unfpa.org/data/world-population-dashboard" target="_blank" rel="noreferrer">unfpa.org/data/world-population-dashboard</a>; HNWI data: New World Wealth</p>
        </div>
      </div>
    </div>
  );
}

export default CountryDataMap;